import { Component, Input, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AppSettings } from 'app/modules/shared/app.settings';
import type { Country } from 'app/modules/shared/models/country';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import * as _ from 'lodash';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { CommonBindingDataService } from '../../../services/common-binding-data.service';
import { ListResponse } from '../../../models/listResponse.models';
import { EntityList } from '../../../models/entity.models';
import { DropdownModule } from 'primeng/dropdown';

@Component({
  selector: 'app-mi-quick-filter-multi-select',
  standalone: true,
  imports: [AutoCompleteModule, FormsModule, ReactiveFormsModule, MultiSelectModule, TranslateModule, CalendarModule, DropdownModule ],
  templateUrl: './mi-quick-filter-multi-select.component.html',
  styleUrl: './mi-quick-filter-multi-select.component.scss'
})
export class MiQuickFilterMultiSelectComponent implements OnInit, OnDestroy {
  @ViewChild('multiSelect') multiSelect!: MultiSelect;
  @Input() field: any;
  formName: FormGroup;
  @Input() attributeLabels: [];
  @Input() moduleName: string;
  @Input() label: string;
  selectedValue!: any[];
  selectAll: boolean = false;
  isFilter: boolean = true;
  placeholder: any;
  filterIcon = 'pi pi-search';
  filterText;
  country: Country;
  items: any[] | undefined;
  multiSelectFields = ['changeReviewStatus', 'entityType', 'eventType', 'eventStatus'];
  private parentFilterSubscription: any;
  noSortingFields = ['order_status', 'can_order'];

  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT_FOR_SETTINGS,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: this.confService.getLoggedInTenantCode(),
    actionStatus: ''
  };
  constructor(private formGroupDirective: FormGroupDirective,
    public cs: CommonBindingDataService,
    private configService: ConfigService,
    private entityService: EntityService,
    private metaDataService: MetaDataService,
    private confService: ConfigService) {
    this.formName = this.formGroupDirective.control;
  }

  ngOnInit(): void {
    if (this.field.presetValues && Array.isArray(this.field.presetValues) && !this.noSortingFields.includes(this.field.attributeCode)) {
      this.field.presetValues.sort((a, b) => {
        const labelA = a.labelValue;
        const labelB = b.labelValue;
        return labelA.toString().localeCompare(labelB.toString());
      });
    }
    if (this.field.attributeCode === 'changeReviewStatus') {
      this.placeholder = this.cs.getLabel('lbl_status');
    } else if (this.field.attributeCode === 'entityType') {
      this.placeholder = this.cs.getLabel('audit_logs.lbl_entity_type');
    } else if (this.field.attributeCode === 'eventType') {
      this.placeholder = this.cs.getLabel('audit_logs.lbl_event_type');
    } else if (this.field.attributeCode === 'eventStatus') {
      this.placeholder = this.cs.getLabel('audit_logs.lbl_event_status');
    } else if (this.field.attributeCode === 'trialStatus') {
      this.placeholder = this.cs.getLabel('reports.lbl_trial_status');
    } else {
      this.placeholder = this.cs.getLabelValue(`${this.moduleName}.fields.${this.field.attributeCode}.placeholder`, this.attributeLabels, this.field.attributeCode);
    }

    if (this.field.attributeCode === 'Parent') {
      let lastUserType = null;
      this.parentFilterSubscription = this.cs.parentFilter$.subscribe((userType) => {
        if (lastUserType !== userType) {
          this.onSelectAllChange({checked: false});
          lastUserType = userType;
          this.field.disabled = false;
          if (userType === 'site' || userType === 'sponsor') {
            this.field.disabled = false;
            this.loadParentOptions(userType);
          } else {
            this.field.disabled = true;
            this.field.presetValues = [];
          }
        }        
      });
    }
  }

  private loadParentOptions(userType: string) {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      limit: AppSettings.DROPDOWN_ROLE_LIMIT,
      offset: 0,
      searchStr: "",
      filters: [],
      countryCode: this.country[0].countryCode,
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
      forTenantCode: this.configService.getForTenantCode(),
      actionStatus: '',
      status: "both"
    };

    const entityCode = userType === 'site' ? AppSettings.ENTITY_CODE.SITE : AppSettings.ENTITY_CODE.SPONSOR;
    this.entityService.searchEntity(entityCode, requestBody, false).subscribe((res: ListResponse) => {
      this.field.presetValues = res.data.map((ele: any) => ({
        labelValue: userType === 'site' ? ele.values.site_name : ele.values.sponsor_name,
        labelKey: ele.id
      })).sort((a, b) => a.labelValue.localeCompare(b.labelValue));
    });
  }

  ngOnDestroy(): void {
    if (this.parentFilterSubscription) {
      this.parentFilterSubscription.unsubscribe();
    }
  }

  searchEntity() {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.entityData.countryCode = this.country[0].countryCode;
    this.entityService.searchEntity(AppSettings.ENTITY_CODE.DRIVER_GROUP, this.entityData).subscribe((res: ListResponse) => {
      this.items = this.cs.getTableData(res);
      this.field.presetValues = _.map(this.items, (item) => ({
        labelKey: item.group_name,
        labelValue: item.id
      })).sort((a, b) => a.labelValue.localeCompare(b.labelValue));
    })
  }

  changedValue(event) {
    const { value } = event;
    this.formName.controls[this.field.attributeCode].setValue(value);
    this.formName.controls[this.field.attributeCode].updateValueAndValidity();
  }

  onSelectAllChange(event) {
    this.selectedValue = event.checked ? [...this.field.presetValues.map(value => value.labelValue)] : [];
    this.formName.controls[this.field.attributeCode].setValue(this.selectedValue);
    this.selectAll = event.checked;
     this.selectedValue = event.checked
    ? [...this.field.presetValues.map(value => value.labelKey)] // ← FIXED
    : [];
  this.formName.controls[this.field.attributeCode].setValue(this.selectedValue);
  this.selectAll = event.checked;
  }

  onFilter(event) {
    if (event.filter) {
      this.filterIcon = 'pi pi-times'
    } else {
      this.filterIcon = 'pi pi-search';
    }
  }

  onFilterIconClick() {
    if (this.filterIcon.includes('times')) {
      this.multiSelect.filterValue = null;
      this.filterIcon = 'pi pi-search';
    }
  }

  clearSearchField(multiSelect: any) {
    multiSelect.filterValue = null;
  }

  search(event: AutoCompleteCompleteEvent) {
    const viewCode = `${this.field.attributeCode.toUpperCase()}_AUTOCOMPLETE_VIEW`;
    this.fetchFromAPI(viewCode, event.query);
  }

  fetchFromAPI(viewCode: string, search: string) {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const requestBody = {
      viewCode,
      countryCode: this.country[0].countryCode,
      forTenantCode: this.configService.getForTenantCode(),
      searchText: search,
      actionStatus: "",
      deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED
    }

    if (this.field.attributeCode === 'driver_group_id') {
      this.moduleName = AppSettings.ENTITY_CODE.DRIVER_GROUP
    }
    this.entityService.autoComplete(AppSettings.ENTITY_CODE[this.moduleName.toUpperCase()], requestBody).subscribe(res => {
      this.items = this.cs.getTableData(res) || [];
    });
  }
}
